import React, { useState, useEffect } from "react";
import {
  Box, Button, TextField, Avatar, Menu, MenuItem, Typography, Autocomplete,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from '@mui/icons-material/Close';
import { useSelector, useDispatch } from "react-redux";
import { getEmployerProfileAndCompany, updateEmployerProfileAndCompany, uploadLogo } from "../services/adminEmployer";
import { CustomAlert, showToastAlert } from "../admin/components/CustomAlert";
import { isValidIndianMobileNumber, validateUrl } from "../common/commonFunctions";
import { getAllPreference } from "../services/getAllPreferences";
import { baseUrl } from "../common/siteSettings";
import { updateStepCompletion } from "../Store/Action";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export default function AddEmployer({ open, handleClose, onComplete = null }) {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.token);

  // Personal Info
  const [empid, setEmpId] = useState("new");
  const [name, setName] = useState("");
  const [mail, setMail] = useState("");
  const [mobile, setMobile] = useState("");
  const [linkedin, setLinkedin] = useState("");

  // Company Info
  const [orgName, setOrgName] = useState("");
  const [orgWebsite, setOrgWebsite] = useState("");
  const [orgAdd, setOrgAdd] = useState("");
  const [orgDes, setOrgDes] = useState("");
  const [numEmp, setNumEmp] = useState("");
  const [selectedPrefs, setSelectedPrefs] = useState([]);
  const [allProf, setAllProf] = useState([]);
  const [logo, setLogo] = useState("");
  const [imgFile, setImageFile] = useState(null);

  const [anchorEll, setAnchorEll] = useState(null);

  useEffect(() => {
    if (open) {
        fetchData();
        if(empid !== "new")
            fetchEmpData();
    }
  }, [open]);

  const fetchEmpData = async () => {
    try {
        const response = await getEmployerProfileAndCompany(token);
        const data = response?.data;
        // Personal Info
        setName(data.employer?.name || "");
        setMail(data.employer?.email || "");
        setMobile(data.employer?.mobile || "");
        setLinkedin(data.employer?.linkedin || "");
    
        // Company Info
        const company = data.employer?.company || {};
        setOrgName(company.name || "");
        setOrgWebsite(company.domain?.url || "");
        setOrgAdd(company.address || "");
        setOrgDes(company.description || "");
        setNumEmp(company.numEmployee || "");
    
        if (company.logo) {
          setLogo(`${baseUrl}/${company.logo}`);
        }
    
        if (company.industry) {
          const prefs = company.industry.map((item) => ({ name: item }));
          setSelectedPrefs(prefs);
        }
    } catch(error) {

    }
    
  }

  const fetchData = async () => {
    try {
        const prefsRes = await getAllPreference();
        setAllProf(prefsRes?.preferences || []);
    } catch (error) {
      console.error(error);
      showToastAlert("Error fetching data", "error");
    }
  };

  const validateFields = () => {
    if (!name.trim()) return { isValid: false, message: "Name is required." };
    if (!mobile.trim() || !isValidIndianMobileNumber(mobile)) return { isValid: false, message: "Valid Mobile Number is required." };
    if (!linkedin.trim() || !validateUrl(linkedin, "linkedin")) return { isValid: false, message: "Valid Linkedin URL is required." };

    if (!orgName.trim()) return { isValid: false, message: "Company Name is required." };
    if (!orgWebsite.trim()) return { isValid: false, message: "Website is required." };
    if (!orgAdd.trim()) return { isValid: false, message: "Company Address is required." };
    if (!orgDes.trim() || orgDes.length < 50) return { isValid: false, message: "Description must be at least 50 characters." };
    if (!numEmp.trim()) return { isValid: false, message: "Number of Employees is required." };
    if (selectedPrefs.length === 0) return { isValid: false, message: "At least one Industry preference is required." };

    return { isValid: true };
  };

  const handlePrefChange = (event, newValue) => {
    setSelectedPrefs(newValue);
  };

  const handleSelect = (value) => {
    setNumEmp(value);
    setAnchorEll(null);
  };

  const validateImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB
      if (file.size > MAX_FILE_SIZE) {
        showToastAlert("File size must not exceed 1 MB", "error");
        return;
      }
      setImageFile(file);

      const reader = new FileReader();
      reader.onload = () => {
        setLogo(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    const validation = validateFields();
    if (!validation.isValid) {
      showToastAlert(validation.message, "error");
      return;
    }

    try {
      let logoPath = logo;
      if (imgFile) {
        const formData = new FormData();
        formData.append("logo", imgFile);

        const logoRes = await uploadLogo(formData, token);
        if (logoRes.data.success) {
          logoPath = logoRes.data.logopath;
        } else {
          showToastAlert("Logo upload failed", "error");
          return;
        }
      }

      const payload = {
        name,
        mobile,
        linkedin,
        email: mail,
        company: {
          name: orgName,
          domain: {url: orgWebsite, verified: false},
          address: orgAdd,
          description: orgDes,
          industries: selectedPrefs.map(item => item.name),
          numEmployee: numEmp
        },
      };

      const response = await updateEmployerProfileAndCompany(token, payload);

      if (response.status === 200) {
        showToastAlert("Profile updated successfully!", "success");
        handleClose();
      } else {
        showToastAlert(response.data.message, "error");
      }
    } catch (error) {
      console.error(error);
      showToastAlert("Update failed", "error");
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle>
        Update Profile & Company Info
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box>
          <Typography variant="h6" mb={1}>Profile Information</Typography>
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth margin="dense" />
          <TextField label="Official Email" value={mail} onChange={(e) => setMail(e.target.value)} fullWidth margin="dense" />
          <TextField label="Mobile Number" value={mobile} onChange={(e) => setMobile(e.target.value)} fullWidth margin="dense" />
          <TextField label="LinkedIn URL" value={linkedin} onChange={(e) => setLinkedin(e.target.value)} fullWidth margin="dense" />

          <Typography variant="h6" mt={3} mb={1}>Company Information</Typography>
          <TextField label="Company Name" value={orgName} onChange={(e) => setOrgName(e.target.value)} fullWidth margin="dense" />
          <TextField label="Website" value={orgWebsite} onChange={(e) => setOrgWebsite(e.target.value)} fullWidth margin="dense" />
          <TextField label="Address" value={orgAdd} onChange={(e) => setOrgAdd(e.target.value)} fullWidth margin="dense" />
          <TextField label="Description" value={orgDes} onChange={(e) => setOrgDes(e.target.value)} fullWidth margin="dense" multiline rows={3} />

          <Autocomplete
            multiple
            size="small"
            options={allProf}
            getOptionLabel={(option) => option.name}
            value={selectedPrefs}
            onChange={handlePrefChange}
            renderInput={(params) => <TextField {...params} label="Industry" margin="dense" />}
          />

          <TextField
            label="Number of Employees"
            value={numEmp}
            fullWidth
            margin="dense"
            onClick={(e) => setAnchorEll(e.currentTarget)}
          />
          <Menu anchorEl={anchorEll} open={Boolean(anchorEll)} onClose={() => setAnchorEll(null)}>
            {["0-50", "51-100", "101-500", "501-1000", "1000+"].map((value) => (
              <MenuItem key={value} onClick={() => handleSelect(value)}>
                {value}
              </MenuItem>
            ))}
          </Menu>

          <Typography variant="h6" mt={2}>Upload Logo</Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar src={logo} sx={{ width: 100, height: 100 }} />
            <Button variant="contained" component="label" startIcon={<CloudUploadIcon />}>
              Upload
              <VisuallyHiddenInput type="file" onChange={validateImage} />
            </Button>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="outlined">Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">Save</Button>
      </DialogActions>

      <CustomAlert />
    </Dialog>
  );
}
